/**
 * Raster Patterns — regex fast-paths for raster layer voice commands.
 *
 * Appended AFTER existing FAST_QUERIES so they don't override existing commands.
 * Each entry: [regex, builder_function]
 * Builder returns an intent object with _fastQuery: true.
 *
 * The `test` property (optional) validates that the captured group is a
 * known raster type — prevents overly greedy matching on non-raster "show" commands.
 */

// ── Known raster type keywords ───────────────────────────────────
const RASTER_TYPES = new Set([
  'ndvi',
  'vegetation',
  'greenness',
  'vegetation index',
  'plant health',
  'landcover',
  'land cover',
  'land use',
  'lulc',
  // NOTE (CL-231): the DEM family ('dem', 'elevation', 'height', 'terrain',
  // 'digital elevation') is deliberately ABSENT. Elevation is served by the
  // ANALYTIC plane (terrain_analysis op — boundary-clipped COGs), never by
  // viz tiles; these patterns run AFTER the terrain fast-queries, and once
  // CL-230 let the terrain builder decline a query (unresolvable division →
  // AI path), a 'dem' keyword here would steal it into render_raster, which
  // has no DEM tiles — a silent no-op instead of Haiku resolving the country.
  'rgb',
  'true color',
  'natural color',
  'satellite',
  'imagery',
  'false color',
  'infrared',
  'ndmi',
  'moisture',
  'ndwi',
  'water index',
  'ndbi',
  'built-up',
  'urban',
  'lst',
  'temperature',
  'surface temperature',
  'thermal',
])

/**
 * Normalize a spoken raster type to its canonical keyword.
 * @param {string} spoken - Raw text from regex capture
 * @returns {string} Canonical type (e.g., 'ndvi', 'landcover', 'dem')
 */
export function normalizeRasterType(spoken) {
  if (!spoken) return ''
  const lower = spoken.toLowerCase().trim()

  const NORMALIZE_MAP = {
    vegetation: 'ndvi',
    greenness: 'ndvi',
    'vegetation index': 'ndvi',
    'plant health': 'ndvi',
    'land cover': 'landcover',
    'land use': 'landcover',
    lulc: 'landcover',
    elevation: 'dem',
    height: 'dem',
    terrain: 'dem',
    'digital elevation': 'dem',
    'true color': 'rgb',
    'natural color': 'rgb',
    satellite: 'rgb',
    imagery: 'rgb',
    'false color': 'false_color',
    infrared: 'false_color',
    moisture: 'ndmi',
    'water index': 'ndwi',
    'built-up': 'ndbi',
    urban: 'ndbi',
    temperature: 'lst',
    'surface temperature': 'lst',
    thermal: 'lst',
  }

  return NORMALIZE_MAP[lower] || lower
}

function isKnownRasterType(text) {
  return RASTER_TYPES.has(text?.toLowerCase().trim())
}

// ── Pattern definitions ──────────────────────────────────────────

export const RASTER_PATTERNS = [
  // "Show NDVI for/of/over/in Nairobi" / "Show land cover of Brazil"
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(.+?)\s+(?:for|of|over|in|across)\s+(.+)/i,
    (m) => ({
      type: 'render_raster',
      layer_type: normalizeRasterType(m[1]),
      location: m[2].trim(),
      _fastQuery: true,
      pipeline: [
        { op: 'navigate', location: m[2].trim() },
        { op: 'render_raster', layer_type: normalizeRasterType(m[1]), location: m[2].trim() },
      ],
    }),
    (m) => isKnownRasterType(m[1]), // test function
  ],

  // "Show NDVI" (no location)
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(.+?)$/i,
    (m) => ({
      type: 'render_raster',
      layer_type: normalizeRasterType(m[1]),
      _fastQuery: true,
      pipeline: [{ op: 'render_raster', layer_type: normalizeRasterType(m[1]) }],
    }),
    (m) => isKnownRasterType(m[1]), // test function
  ],

  // "Show NDVI and land cover of Kenya"
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(.+?)\s+and\s+(.+?)\s+(?:for|of|over|in|across)\s+(.+)/i,
    (m) => ({
      type: 'render_raster',
      _fastQuery: true,
      pipeline: [
        { op: 'navigate', location: m[3].trim() },
        { op: 'render_raster', layer_type: normalizeRasterType(m[1]), location: m[3].trim() },
        { op: 'render_raster', layer_type: normalizeRasterType(m[2]), location: m[3].trim() },
      ],
    }),
    (m) => isKnownRasterType(m[1]) && isKnownRasterType(m[2]), // test function
  ],

  // "Hide/remove NDVI" / "Hide the land cover layer"
  [
    /^(?:hide|remove|clear)\s+(?:the\s+)?(.+?)(?:\s+layer)?$/i,
    (m) => ({
      type: 'remove_raster',
      layer_type: normalizeRasterType(m[1]),
      _fastQuery: true,
      pipeline: [{ op: 'remove_raster', layer_type: normalizeRasterType(m[1]) }],
    }),
    (m) => isKnownRasterType(m[1].replace(/\s+layer$/i, '')), // test function
  ],

  // "Hide all raster layers" / "Remove all layers"
  [
    /^(?:hide|remove|clear)\s+all\s+(?:raster\s+)?layers?$/i,
    () => ({
      type: 'remove_raster',
      _fastQuery: true,
      pipeline: [{ op: 'remove_raster' }],
    }),
  ],

  // "Make NDVI more/less transparent/opaque"
  [
    /^(?:make)\s+(?:the\s+)?(.+?)\s+(more|less)\s+(transparent|opaque)$/i,
    (m) => {
      const moreTransparent =
        (m[2] === 'more' && m[3] === 'transparent') || (m[2] === 'less' && m[3] === 'opaque')
      const delta = moreTransparent ? -0.2 : 0.2
      return {
        type: 'adjust_opacity',
        layer_type: normalizeRasterType(m[1]),
        _fastQuery: true,
        pipeline: [{ op: 'adjust_opacity', layer_type: normalizeRasterType(m[1]), delta }],
      }
    },
    (m) => isKnownRasterType(m[1]), // test function
  ],

  // "Set NDVI opacity to 50%" / "Set land cover opacity to 0.7"
  [
    /^set\s+(?:the\s+)?(.+?)\s+opacity\s+to\s+(\d+)%?$/i,
    (m) => {
      let opacity = parseFloat(m[2])
      if (opacity > 1) opacity = opacity / 100 // Convert percentage
      return {
        type: 'adjust_opacity',
        layer_type: normalizeRasterType(m[1]),
        _fastQuery: true,
        pipeline: [{ op: 'adjust_opacity', layer_type: normalizeRasterType(m[1]), opacity }],
      }
    },
    (m) => isKnownRasterType(m[1]), // test function
  ],
]
